import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Input, LoadingSpinner } from '../components';
import { authService } from '../services';

/**
 * RegisterPage - The Gate (Screen 02 - Registration)
 * Design: Black background, white form, minimalist
 * Purpose: New user registration and credential creation
 */
export default function RegisterPage() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState({});
  const [generalError, setGeneralError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear specific error when user types
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
    if (generalError) setGeneralError('');
  };

  const validateForm = () => {
    const newErrors = {};

    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    // Confirm password validation
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGeneralError('');

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // Use authService for registration
      const result = await authService.register(
        formData.email,
        formData.password,
        formData.phone || null
      );

      if (result.success) {
        // Redirect to mode select (choosing partygoer vs promoter)
        // The authService already stored the tokens, so we just need to reload
        window.location.href = '/mode-select';
      } else {
        setGeneralError(result.error || 'Registration failed. Please try again.');
      }
    } catch (err) {
      setGeneralError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center p-4 md:p-8">
      {/* Back to landing */}
      <Link
        to="/"
        className="absolute top-8 left-8 font-mono text-sm uppercase tracking-wider text-gray-400 hover:text-white transition-colors"
      >
        &larr; Back
      </Link>

      {/* Registration form container */}
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-mono text-3xl md:text-4xl font-bold uppercase tracking-wider text-white mb-4">
            Join the Party
          </h1>
          <p className="font-mono text-sm uppercase tracking-wider text-gray-400">
            Create your credential
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white p-8 md:p-12">
          {/* General error message */}
          {generalError && (
            <div className="mb-6 p-4 bg-red-50 border-2 border-red-600">
              <p className="font-mono text-sm text-red-600 uppercase tracking-wider">
                {generalError}
              </p>
            </div>
          )}

          {/* Email field */}
          <div className="mb-6">
            <Input
              label="Email"
              id="email"
              name="email"
              type="email"
              placeholder="your@email.com"
              value={formData.email}
              onChange={handleChange}
              error={errors.email}
              required
              disabled={isLoading}
            />
          </div>

          {/* Phone field (optional) */}
          <div className="mb-6">
            <Input
              label="Phone"
              id="phone"
              name="phone"
              type="tel"
              placeholder="+1 (555) 000-0000"
              value={formData.phone}
              onChange={handleChange}
              error={errors.phone}
              disabled={isLoading}
            />
            <p className="mt-2 font-mono text-xs text-gray-500 uppercase tracking-wider">
              Optional - for event notifications
            </p>
          </div>

          {/* Password field */}
          <div className="mb-6">
            <Input
              label="Password"
              id="password"
              name="password"
              type="password"
              placeholder="Min. 8 characters"
              value={formData.password}
              onChange={handleChange}
              error={errors.password}
              required
              disabled={isLoading}
            />
          </div>

          {/* Confirm password field */}
          <div className="mb-8">
            <Input
              label="Confirm Password"
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              placeholder="Re-enter your password"
              value={formData.confirmPassword}
              onChange={handleChange}
              error={errors.confirmPassword}
              required
              disabled={isLoading}
            />
          </div>

          {/* Register button */}
          <Button
            type="submit"
            variant="primary"
            fullWidth
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <LoadingSpinner size="sm" />
                Creating...
              </span>
            ) : (
              'Create Account'
            )}
          </Button>
        </form>

        {/* Login link */}
        <div className="mt-8 text-center">
          <p className="font-mono text-sm uppercase tracking-wider text-gray-400">
            Already have credentials?{' '}
            <Link
              to="/login"
              className="text-white hover:underline"
            >
              Login
            </Link>
          </p>
        </div>
      </div>

      {/* Decorative corners */}
      <div className="absolute top-8 right-8 w-16 h-16 border-r-2 border-t-2 border-white opacity-20" />
      <div className="absolute bottom-8 left-8 w-16 h-16 border-l-2 border-b-2 border-white opacity-20" />
      <div className="absolute bottom-8 right-8 w-16 h-16 border-r-2 border-b-2 border-white opacity-20" />
    </div>
  );
}
